import React from "react";
//#region components
import { faqArticleSlug, faqArticleUrl, MarkdownLinkPattern } from "./agentFaqLinks";
import useCurrentAgentConversation from "./useCurrentAgentConversation";
import { BoldPattern, toBlocks } from "./agentMarkdown";
import { modeOf } from "./agentFlow";
//#endregion
import cssExports from "theme/styles/main.module.scss";

interface AgentAnswerTextProps {
  /** Markdown, limited by the agent to bold, bullet lists, line breaks and links to the FAQ articles it used. */
  text: string;
}

const withBold = (line: string): React.ReactNode[] =>
  line.split(BoldPattern).map((part, index) => (index % 2 ? <strong key={index}>{part}</strong> : part));

// A link to an FAQ article becomes a link that opens in a new tab; any other keeps only its text.
const withInline = (line: string, onFaqOpened: (address: string) => void): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let from = 0;
  for (const match of line.matchAll(MarkdownLinkPattern)) {
    const [whole, label, address] = match;
    const index = match.index ?? 0;
    const href = faqArticleUrl(address);
    nodes.push(<React.Fragment key={`text${from}`}>{withBold(line.slice(from, index))}</React.Fragment>);
    nodes.push(
      href ? (
        <a
          key={`link${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cssExports.linkText}
          onClick={() => onFaqOpened(address)}
        >
          {withBold(label)}
        </a>
      ) : (
        <React.Fragment key={`link${index}`}>{withBold(label)}</React.Fragment>
      )
    );
    from = index + whole.length;
  }
  nodes.push(<React.Fragment key={`text${from}`}>{withBold(line.slice(from))}</React.Fragment>);
  return nodes;
};

// The agent's answer is written by a model that reads customer data, so only bold, lists, line breaks
// and links to FAQ articles are rendered and everything else stays text: any other link, a bare URL or
// HTML is never followed.
const AgentAnswerText: React.FC<AgentAnswerTextProps> = ({ text }) => {
  const { conversation } = useCurrentAgentConversation();
  const onFaqOpened = (address: string) =>
    conversation.track("faq.opened", {
      slug: faqArticleSlug(address),
      from: modeOf(conversation.getSnapshot()),
    });

  return (
    <div className={cssExports.agentAnswerText}>
      {toBlocks(text)
        .filter((block) => (block.kind === "list" ? block.items.length : block.lines.length))
        .map((block, index) =>
          block.kind === "list" ? (
            <ul key={index}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{withInline(item, onFaqOpened)}</li>
              ))}
            </ul>
          ) : (
            <p key={index}>
              {block.lines.map((line, lineIndex) => (
                <React.Fragment key={lineIndex}>
                  {lineIndex > 0 ? <br /> : null}
                  {withInline(line, onFaqOpened)}
                </React.Fragment>
              ))}
            </p>
          )
        )}
    </div>
  );
};

export default React.memo(AgentAnswerText);
